import { useCallback, useMemo, useState } from 'react'

import filterDefaults from 'consts/filterDefaults'
import { useAuth } from 'contexts/AuthContext'
import type { ProviderKey } from 'entities/provider'
import { useTranslate } from 'lib/tolgee'

export interface ProviderOption {
  value: ProviderKey
  label: string
  disabled: boolean
}

export function useProviderOptions() {
  const { plan } = useAuth()
  const t = useTranslate()

  const checkIsProviderDisabled = useCallback(
    (providerValue: string) => {
      const availableProviders = plan?.providers ?? []
      const generalProviderValue = filterDefaults.trendingTags.generalProvider.value

      return providerValue !== generalProviderValue && !availableProviders.includes(providerValue)
    },
    [plan?.providers]
  )

  const providerOptions: ProviderOption[] = Array.from(
    filterDefaults.trendingTags.providers as Array<{ label: any; value: string }>
  ).map(provider => {
    return {
      label: provider.value === 'all' ? t(provider.label) : (provider.label as string),
      value: provider.value as ProviderKey,
      disabled: checkIsProviderDisabled(provider.value),
    }
  })

  const [selectedProvider, setSelectedProvider] = useState(() => {
    const generalProviderValue = filterDefaults.trendingTags.generalProvider.value
    return providerOptions.find(provider => provider.value === generalProviderValue)
  })

  const selectProvider = useCallback(
    (provider: ProviderOption) => {
      setSelectedProvider(provider)
    },
    [setSelectedProvider]
  )

  const memoizedValues = useMemo(
    () => ({
      providerOptions,
      selectedProvider,
      selectProvider,
    }),
    [providerOptions, selectProvider, selectedProvider]
  )

  return memoizedValues
}
